import type { SiteLang } from "@/hooks/useLanguage";

type TeamI18n = {
  seo: { title: string; description: string };
  breadcrumb: { about: string; team: string };
  hero: {
    kicker: string;
    title: string;
    p1: string;
    p2: string;
    ctaLeadership: string;
    ctaDivisions: string;
  };
  executive: { kicker: string; title: string; subtitle: string };
  groupPhoto: { overlay: string };
  division: { kicker: string; title: string; subtitle: string };
  experts: { kicker: string; title: string; subtitle: string };
  whyEngage: {
    kicker: string;
    title: string;
    subtitle: string;
    items: { title: string; desc: string }[];
    ctaKnowledge: string;
  };
};

const en: TeamI18n = {
  seo: {
    title: "Our Team | QbD Group Leadership",
    description: "Meet the leadership team behind QbD Group, over 600 expert consultants across 11 offices, led by seasoned professionals driving strategic vision in life sciences.",
  },
  breadcrumb: { about: "About us", team: "Our team" },
  hero: {
    kicker: "Global consulting network",
    title: "Meet our team",
    p1: "Our team of over <strong class=\"text-primary-foreground\">600 expert consultants</strong> globally, QbD Group offers extensive knowledge and solutions for complex life science project challenges.",
    p2: "Alongside our headquarters in Belgium, we have established affiliates in the Netherlands, Spain, France, the UK, Switzerland, Poland, Colombia, and the US.",
    ctaLeadership: "Meet the leadership",
    ctaDivisions: "Explore our Centers of Excellence",
  },
  executive: {
    kicker: "Driving strategic vision",
    title: "Executive team",
    subtitle: "Led by a group of seasoned professionals, our Executive Team drives QbD Group's strategic vision and ensures we stay at the forefront of industry advancements.",
  },
  division: {
    kicker: "Specialist-led operations",
    title: "Center of Excellence Heads",
    subtitle: "The heads of the QbD Group Centers of Excellence are experts in their respective fields, overseeing operations and delivering high-quality solutions across various industries.",
  },
  experts: {
    kicker: "Thought leadership",
    title: "Featured experts",
    subtitle: "A spotlight on just a few of the 700+ specialists across QbD Group who share their knowledge through webinars, white papers, and industry events.",
  },
  groupPhoto: { overlay: "700+ experts across 11 offices worldwide" },
  whyEngage: {
    kicker: "Webinars and industry insights",
    title: "Why engage with our experts?",
    subtitle: "Our thought leadership platform is designed with your needs in mind. By engaging with our experts, you gain access to:",
    items: [
      { title: "Industry-leading insights", desc: "Stay informed about the latest trends, innovations, and solutions in life sciences." },
      { title: "Credible expertise", desc: "Trust in the knowledge and experience of industry leaders who understand your challenges." },
      { title: "Practical solutions", desc: "Get actionable advice to improve compliance, innovation, and operational efficiency." },
      { title: "Strategic advantage", desc: "Position your business to lead in your field with reliable insights from QbD Group." },
    ],
    ctaKnowledge: "Explore our Knowledge Center",
  },
};

const es: TeamI18n = {
  seo: {
    title: "Nuestro equipo | Liderazgo de QbD Group",
    description: "Conoce al equipo directivo de QbD Group, más de 600 consultores expertos en 11 oficinas, liderados por profesionales que impulsan la visión estratégica en Life Sciences.",
  },
  breadcrumb: { about: "Sobre nosotros", team: "Nuestro equipo" },
  hero: {
    kicker: "Consultoría con alcance global",
    title: "Conoce a nuestro equipo",
    p1: "Con un equipo de más de <strong class=\"text-primary-foreground\">600 consultores expertos</strong> a nivel global, QbD Group ofrece amplios conocimientos y soluciones para los retos más complejos de proyectos en Life Sciences.",
    p2: "Junto con nuestra sede central en Bélgica, contamos con filiales en Países Bajos, España, Francia, Reino Unido, Suiza, Polonia, Colombia y Estados Unidos.",
    ctaLeadership: "Conoce al liderazgo",
    ctaDivisions: "Explora nuestros Centros de Excelencia",
  },
  executive: {
    kicker: "Impulso a la estrategia",
    title: "Equipo directivo",
    subtitle: "Liderado por un grupo de profesionales experimentados, nuestro equipo directivo impulsa la visión estratégica de QbD Group y asegura que nos mantengamos a la vanguardia de los avances del sector.",
  },
  division: {
    kicker: "Supervisión por especialidad",
    title: "Directores de los Centros de Excelencia",
    subtitle: "Los directores de los Centros de Excelencia de QbD Group son expertos en sus respectivos campos, supervisando las operaciones y entregando soluciones de alta calidad en diversas industrias.",
  },
  experts: {
    kicker: "Liderazgo de pensamiento",
    title: "Expertos destacados",
    subtitle: "Una muestra de los más de 600 especialistas de QbD Group que comparten su conocimiento a través de webinars, white papers y eventos del sector.",
  },
  groupPhoto: { overlay: "Más de 700 expertos en 11 oficinas en todo el mundo" },
  whyEngage: {
    kicker: "Webinars y tendencias",
    title: "¿Por qué colaborar con nuestros expertos?",
    subtitle: "Nuestra plataforma de liderazgo de pensamiento está diseñada pensando en tus necesidades. Al colaborar con nuestros expertos, accedes a:",
    items: [
      { title: "Conocimiento líder en la industria", desc: "Mantente informado sobre las últimas tendencias, innovaciones y soluciones en Life Sciences." },
      { title: "Experiencia reconocida", desc: "Confía en el conocimiento y la experiencia de líderes del sector que entienden tus retos." },
      { title: "Soluciones prácticas", desc: "Obtén asesoramiento accionable para mejorar el cumplimiento, la innovación y la eficiencia operativa." },
      { title: "Ventaja estratégica", desc: "Posiciona tu negocio como líder en tu campo con información fiable de QbD Group." },
    ],
    ctaKnowledge: "Explora nuestro Knowledge Center",
  },
};

const zh: TeamI18n = {
  seo: {
    title: "我们的团队 | QbD Group 领导层",
    description: "认识 QbD Group 背后的领导团队——700多名专家顾问遍布11个办事处，由经验丰富的专业人员引领生命科学领域的战略愿景。",
  },
  breadcrumb: { about: "关于我们", team: "我们的团队" },
  hero: {
    kicker: "全球布局跨域顾问",
    title: "认识我们的团队",
    p1: "我们的全球团队由 <strong class=\"text-primary-foreground\">700多名专家顾问</strong> 组成，QbD Group 为复杂的生命科学项目挑战提供广泛的知识和解决方案。",
    p2: "除了位于比利时的总部外，我们还在荷兰、西班牙、法国、英国、瑞士、波兰、哥伦比亚和美国设立了分支机构。",
    ctaLeadership: "认识领导团队",
    ctaDivisions: "查看各卓越中心",
  },
  executive: {
    kicker: "战略引领持续创新",
    title: "执行团队",
    subtitle: "在一群经验丰富的专业人员领导下，我们的执行团队推动 QbD Group 的战略愿景，并确保我们始终走在行业前沿。",
  },
  division: {
    kicker: "专业能力",
    title: "卓越中心负责人",
    subtitle: "QbD Group 各卓越中心的负责人是各自领域的专家，监督运营并在多个行业提供高质量解决方案。",
  },
  experts: {
    kicker: "思想领导力",
    title: "重点专家",
    subtitle: "聚焦 QbD Group 700多位专家中的部分代表，他们通过网络研讨会、白皮书和行业活动分享专业知识。",
  },
  groupPhoto: { overlay: "全球11个办事处，700多名专家" },
  whyEngage: {
    kicker: "行业洞察实践交流",
    title: "为什么与我们的专家合作？",
    subtitle: "我们的思想领导力平台以您的需求为出发点。与我们的专家合作，您将获得：",
    items: [
      { title: "行业领先洞察", desc: "及时掌握生命科学领域的最新趋势、创新和解决方案。" },
      { title: "可信赖的专业能力", desc: "信赖了解您挑战的行业领导者所拥有的知识与经验。" },
      { title: "切实可行的解决方案", desc: "获得可落地的建议，提升合规、创新和运营效率。" },
      { title: "战略优势", desc: "借助 QbD Group 的可靠洞察，让您的业务在所在领域保持领先。" },
    ],
    ctaKnowledge: "浏览我们的知识中心",
  },
};

const translations: Record<SiteLang, TeamI18n> = { en, es, "zh-cn": zh };
export const getTeamT = (lang: SiteLang) => translations[lang] ?? en;
